import {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import { Trans } from 'react-i18next'
import { useDispatch } from 'react-redux'

import { FormHandles } from '@unform/core'
import { Alert, IconPencil } from '@vtex/shoreline'

import { AlertCustom } from '~/components/AlertCustom'
import { Button } from '~/components/Buttons'
import EmptyPage from '~/components/empty-page'
import { FilterSearch } from '~/components/FilterSearch'
import Loading from '~/components/Loading'
import { FormGroup, Label, Textarea } from '~/components/unform'
import { useAppInfo } from '~/hooks/useAppInfo'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'
import {
  MAX_RECOMMENDED_PRODUCTS,
  MIN_RECOMMENDED_PRODUCTS
} from '~/modules/retailMedia/constants/objectiveBased'
import * as types from '~/modules/retailMedia/store/product/constants'
import { useAppSelector } from '~/store/hooks'

import {
  addProducts,
  deleteAllProductsOnCampaign,
  deleteProductOnCampaign
} from '../../store/campaign/actions'
import { listProducts } from '../../store/product/actions'
import AlertBrands from '../AlertBrands'
import Product from '../Product'
import ProductsPanelTabs from '../ProductsPanelTabs'

import * as S from './styles'

interface Props {
  formRef?: MutableRefObject<FormHandles>
  isRequired?: boolean
  description?: string
  isEdit?: boolean
  isCollapsible?: boolean
  publisherId: string
  onResetList?: () => void
  recommendedProductsQtd: number
}

const SelectedProducts = ({
  formRef,
  isEdit: initialIsEdit = false,
  publisherId,
  onResetList,
  recommendedProductsQtd
}: Props) => {
  const [isEdit, setIsEdit] = useState(initialIsEdit)
  const { t } = useAPPTranslation()

  const { isWhiteLabel } = useAppInfo()

  /**
   * Lógica para a funcionalidade principal
   */

  const [bulkProducts, setBulkProducts] = useState<string>('')
  const [activeTab, setActiveTab] = useState<SearchProductBy>('search')

  // Busca de produto
  const dispatch = useDispatch()

  const {
    campaign: {
      products: { loadingAddBulk, rejectedProducts = null }
    },
    product: {
      list: { data: searchProducts, loading: listProductsLoading }
    }
  } = useAppSelector(state => state.retailMedia)

  /**
   * REFACTOR
   * Essa const está sendo usada para verificar se tem produtos sugeridos
   * Seria ideal termos algo mais específico para isso
   */
  const allProducts = useAppSelector(
    ({ retailMedia }) => retailMedia.campaign.products.selectedProducts
  )

  const { query } = useAppSelector(state => state.filters)

  const selectedSKUS = useMemo(
    () => allProducts.map(item => item.product_sku),
    [allProducts]
  )
  const selectedIDs = useMemo(
    () => allProducts.map(item => item.id),
    [allProducts]
  )
  const itemsDisableds = useMemo(
    () => allProducts.filter(item => item.item_disabled),
    [allProducts]
  )

  const filteredQuery = useMemo(
    () => query?.[`${types.PRODUCT_REDUX_QUERY}`] || null,
    [query]
  )

  const queryStringSearchProduct = useMemo(
    () => ({
      quantity: 1000,
      query: filteredQuery,
      publisher_id: publisherId
    }),
    [filteredQuery, publisherId]
  )

  useEffect(() => {
    if (allProducts.length === 0) {
      setIsEdit(true)
    }
  }, [allProducts])

  useEffect(() => {
    if (formRef?.current?.setFieldValue) {
      formRef.current.setFieldValue('products', allProducts)
    }
  }, [allProducts, formRef])

  useEffect(() => {
    if (
      activeTab === 'search' &&
      filteredQuery &&
      (publisherId || isWhiteLabel)
    ) {
      dispatch(listProducts(queryStringSearchProduct))
    }
  }, [
    activeTab,
    dispatch,
    filteredQuery,
    publisherId,
    queryStringSearchProduct,
    isWhiteLabel
  ])

  const handleDeleteAllProducts = useCallback(
    () => dispatch(deleteAllProductsOnCampaign(isEdit)),
    [dispatch, isEdit]
  )

  const handleCancel = useCallback(() => {
    setIsEdit(false)
  }, [])

  const handleChangeProductsBulk = useCallback(
    (e: { target: { value: string } }) => {
      const data = e.target.value
      setBulkProducts(data)
    },
    []
  )

  const handleAddProductsBulk = useCallback(() => {
    const queryKey: AddProductsBodyQueryKey = {
      search: 'query',
      product_skus: 'product_skus',
      // Backend contract: even in the product_ids UI tab, bulk payload key stays product_skus.
      product_ids: 'product_skus',
      product_gtins: 'product_gtins',
      product_additional_references: 'product_additional_references',
      product_reference_codes: 'product_reference_codes'
    }

    const body = {
      publisher_id: publisherId,
      page: 0,
      products: bulkProducts,
      isEdit,
      queryKey: queryKey?.[activeTab] || 'query'
    }

    dispatch(addProducts({ body }))

    formRef.current.setFieldValue('bulkProducts', '')
  }, [activeTab, bulkProducts, dispatch, formRef, isEdit, publisherId])

  const handleTabs = useCallback(
    (tabKey: SearchProductBy) => setActiveTab(tabKey),
    []
  )

  const removeNotRecommendedProducts = useCallback(() => {
    const productsToRemove = allProducts.filter(item => !item.isSuggested)
    productsToRemove.forEach(product => {
      dispatch(deleteProductOnCampaign({ product }))
    })
  }, [allProducts, dispatch])

  const handleRestoreRecommendedProducts = useCallback(() => {
    onResetList && onResetList()
    removeNotRecommendedProducts()
    setIsEdit(false)
  }, [onResetList, removeNotRecommendedProducts])

  const hasRecommendedProductsQtd = useMemo(
    () =>
      allProducts?.length >= MIN_RECOMMENDED_PRODUCTS &&
      allProducts?.length <= MAX_RECOMMENDED_PRODUCTS,
    [allProducts]
  )

  const hasRecommendedProductsChanges = useMemo(() => {
    return (
      allProducts?.some(item => !item?.isSuggested) ||
      allProducts?.length !== recommendedProductsQtd
    )
  }, [allProducts, recommendedProductsQtd])

  const shouldShowRestoreButton = useMemo(() => {
    return (
      onResetList &&
      isEdit &&
      hasRecommendedProductsChanges &&
      recommendedProductsQtd > 0
    )
  }, [
    onResetList,
    isEdit,
    hasRecommendedProductsChanges,
    recommendedProductsQtd
  ])

  return (
    <>
      <S.Header>
        {!hasRecommendedProductsQtd && (
          <Alert variant="warning">
            {t('rm:objectiveBased.selectedProducts.alert', {
              minProducts: MIN_RECOMMENDED_PRODUCTS,
              maxProducts: MAX_RECOMMENDED_PRODUCTS
            })}
          </Alert>
        )}
        <AlertBrands publisherId={publisherId} />
      </S.Header>

      {allProducts.length > 0 && !isEdit && (
        <AlertCustom
          text={t('rm:objectiveBased.AutomaticallySelectedProducts.info')}
          className="mt-2 mb-2"
        />
      )}

      {/* Lista de pesquisa */}
      <S.ProductsLists>
        {isEdit && (
          <S.ProductsList>
            <S.ProductsListHeader>
              <ProductsPanelTabs
                activeTab={activeTab}
                publisherId={publisherId}
                onChange={handleTabs}
              />

              {activeTab === 'search' && (
                <FilterSearch
                  placeholder={t('rm:filter.searchProductsPlaceholder')}
                  keyState={`${types.PRODUCT_REDUX_QUERY}`}
                  live
                  isDebounce
                />
              )}

              <Loading status={listProductsLoading}>
                {t('rm:ProductsPanel.loadingProducts')}...
              </Loading>

              {/* Esta seção permitirá o usuário adicionar vários produtos de uma vez */}
              {activeTab !== 'search' && (
                <S.BulkSection>
                  <Loading status={loadingAddBulk}>
                    {t('rm:ProductsPanel.addingProducts')}...
                  </Loading>

                  {!!rejectedProducts?.length && (
                    <div className="alertsForm" key={`errorOn_${activeTab}`}>
                      <Alert variant="warning">
                        {t('rm:ProductsPanel.alert.addProductError', {
                          codes: rejectedProducts.join(', ') as string
                        })}
                      </Alert>
                    </div>
                  )}

                  <FormGroup>
                    <Label htmlFor="bulkProducts" isRequired>
                      {t('rm:ProductsPanel.bulkProducts.label')}
                    </Label>
                    <Textarea
                      name="bulkProducts"
                      placeholder={t(
                        'rm:ProductsPanel.bulkProducts.placeholder'
                      )}
                      onChange={handleChangeProductsBulk}
                    />
                  </FormGroup>

                  <FormGroup className="actionButtons">
                    <Button
                      text={t('common:actions.add')}
                      onClick={handleAddProductsBulk}
                      disabled={!bulkProducts}
                    />
                  </FormGroup>
                </S.BulkSection>
              )}
            </S.ProductsListHeader>

            {!listProductsLoading &&
              activeTab === 'search' &&
              filteredQuery && (
                <div>
                  {searchProducts?.map(item => (
                    <Product
                      hasAction
                      key={item.id}
                      data={item}
                      selectedSKUS={selectedSKUS}
                      selectedIDs={selectedIDs}
                      itemsDisableds={itemsDisableds}
                    />
                  ))}
                </div>
              )}
          </S.ProductsList>
        )}

        {/* Lista de selecionadas */}
        <S.ProductsList>
          <S.ProductsListHeader>
            <S.Heading>
              <S.Title>
                <Trans
                  i18nKey="rm:ProductsPanel.selectedProductsLength"
                  defaults="Produtos selecionados <span>({{count}} produtos)</span>"
                  components={{ span: <span /> }}
                  values={{ count: allProducts.length || 0 }}
                />
              </S.Title>

              <aside>
                {shouldShowRestoreButton && (
                  <Button
                    text={t('rm:ProductsPanel.restoreSuggestedProducts')}
                    onClick={handleRestoreRecommendedProducts}
                    size="xsmall"
                    template="transparentPrimary"
                    className="me-2"
                    customWidth="auto"
                  />
                )}

                {isEdit && !!allProducts?.length && (
                  <Button
                    template="transparentCritical"
                    text={t('c:actions.removeAll')}
                    onClick={handleDeleteAllProducts}
                    size="xsmall"
                    customWidth="auto"
                  />
                )}

                {!isEdit && (
                  <Button
                    iconLeft={<IconPencil />}
                    template="transparentOutlined"
                    text={t('rm:button.Edit')}
                    onClick={() => setIsEdit(true)}
                    size="xsmall"
                  />
                )}
              </aside>
            </S.Heading>
          </S.ProductsListHeader>

          <div>
            {allProducts?.map(item => (
              <Product hasAction key={item.id} data={item} isSelected />
            ))}
          </div>

          {allProducts.length === 0 && (
            <div className="empty">
              <EmptyPage
                title={t('rm:ProductsPanel.empty.title')}
                subtitle={t('rm:ProductsPanel.empty.subtitle')}
                svgPath="photos/orders-empty"
                // {...(onResetList && {
                //   onClick: onResetList,
                //   titleButton: 'Restaurar produtos sugeridos'
                // })}
              />
            </div>
          )}
        </S.ProductsList>
      </S.ProductsLists>

      {/* Esse botão não está cancelando de fato. Mantém os produtos escolhidos */}
      {/* {isEdit && (
        <S.WrapperButtons className="border-top border-bottom py-2">
          <Button
            template="secondary"
            text={t('rm:button.Cancel')}
            onClick={handleCancel}
            size="small"
          />
        </S.WrapperButtons>
      )} */}
    </>
  )
}

export default SelectedProducts
